import { ZodError } from "zod";
import LoadingSpinner from '@c/LoadingSpinner';
import CategorySkeleton from '@c/skeletons/CategorySkeleton';
import { FirebaseError } from '@firebase/app';
import {
  CreatePaymentArgs,
  MinimalOrderDocument,
  UpdatePaymentArgs,
} from '@util/firestore/payments/payments.types';
import { getOrderId } from './firestore/orders';
import { getProductById } from './firestore/products';
import { Coupon } from './firestore/stripe';
import { AddressDocument } from './types/firestore/address';
import { CartItem, cartItemSchema } from './types/firestore/carts';
import {
  OrderDocument,
  OrderItemDocument,
  orderItemDocumentSchema,
} from './types/firestore/order';
import { ProductDocument } from './types/firestore/products';
import { UserDocument } from './types/firestore/users';
import { parsePhoneNumber } from 'libphonenumber-js';
import { getAttributionForStripe } from '@util/marketing';
export const IMAGE_PATH = 'https://gearfocus.b-cdn.net/' as const;
export const IMAGE_EXT = '.jpeg' as const;


export function getLoadingIndicator(v?: LoadingIndicatorVariants) {
  if (v === 'categorySkeleton') return CategorySkeleton;
  return LoadingSpinner;
}

type LoadingIndicatorVariants = 'spinner' | 'categorySkeleton';

export const getCartItemFromProductDocument = (
  product: ProductDocument,
  pricing?: { unitCents: number; applied: { saleId: string } | null }
): CartItem | null => {
  try {
    return cartItemSchema.parse({
      product_id: product.id,
      product_sku: product.sku,
      product_title: product.title,
      product_image: product.thumbnail,
      product_link: product.slug,
      product_cost: pricing?.applied ? pricing.unitCents / 100 : product.price,
      ...(pricing?.applied ? { store_sale_id: pricing.applied.saleId } : {}),
      product_brand: product.brand,
      product_categories: product.categories,
      product_master: product.gearMaster?.[0] || "",
      external_source: product.external_source || "gearfocus",
      seller_id: product.seller_id,
      qty: 1,
      account_id: product.account_id,
      is_flat_rate: product.is_flat_rate,
    });
  } catch (error) {
    if (error instanceof ZodError) {
      console.error("Zod validation error:", error?.errors);
    } else {
      console.error("Unexpected error:", error); // Log unexpected errors
    }
    return null; // Return null or handle appropriately in case of an error
  }
};

export const cartItemToProductDocument = async (
  item: CartItem
): Promise<ProductDocument | null> => {
  return getProductById(item.product_id);
};

export const cartItemToOrderItem = (
  item: CartItem & { rate_id?: string } //TODO: maybe add rate_id to cartItemSchema?
): OrderItemDocument => {
  return orderItemDocumentSchema.parse({
    id: item.product_id,
    qty: item.qty,
    state: 8, //Set Initial State For Item To "Processing"
    product_cost: item.product_cost,
    ...(item.store_sale_id ? { store_sale_id: item.store_sale_id } : {}),
    buyer_shipping_cost: item.shipping_cost ?? 0,
    rate_id: item.rate_id,
    // Product details for analytics tracking
    product_title: item.product_title,
    product_sku: item.product_sku,
    product_brand: item.product_brand,
    product_image: item.product_image,
    product_categories: item.product_categories,
    external_source: item.external_source,
  });
};

export function formatAddress(address: AddressDocument): string {
  return `${address.address_line1}, ${address.city_locality} ${address.state_province} ${address.postal_code}, ${address.country_code}`;
}

const metaData = (order: MinimalOrderDocument, isGuest: boolean) => {
  // Per-item JSON used to be packed into metadata here, but Stripe enforces
  // a 500-char-per-value limit and long CDN URLs blew past it (GB #1294).
  // Server-side routes (update-payment, pay-with-balance) now write the full
  // cart to pending_orders Firestore and the paymentSuccess webhook reads
  // from there. Keep this function returning only safe scalars.

  // Get marketing attribution data from cookie (with safety wrapper)
  let attribution: Record<string, string> = {};
  try {
    attribution = getAttributionForStripe();
  } catch (e) {
    // Silently fail - attribution is not critical to checkout
    console.error('[Attribution] Failed to get attribution for Stripe:', e);
  }

  return {
    order_id: order.id ?? '',
    buyer_id: order.buyer_id ?? '',
    buyer_email: order.buyer_email,
    shipping_cost: order.shipping_total ? order.shipping_total.toFixed(2) : '0',
    ...(order.donate && { donation: 'R2R' }),
    ...(order.coupon?.id && { promo: order.coupon?.id }),
    isGuest: String(isGuest),
    // Marketing attribution fields
    ...attribution,
  };
};

export function getCreatePaymentIntentArgs({
  items,
  userDoc,
  isGuest,
}: {
  items: CartItem[];
  userDoc?: UserDocument;
  isGuest: boolean;
}): CreatePaymentArgs {
  console.info("getCreatePaymentIntentArgs")

  const sellers = Object.fromEntries(
    items.map((item) => [item.seller_id, [cartItemToOrderItem(item)]])
  );
  let calculatedTotal = 0;
  items.forEach((item) => {
    calculatedTotal += item.product_cost * item.qty + (item.shipping_cost ?? 0);
  });
  const item_count = items.reduce((acc, item) => acc + item.qty, 0);

  // not including all of the Order fields, only the ones needed for creating a payment intent
  const order = {
    sellers,
    total: calculatedTotal,
    subtotal: calculatedTotal,
    shipping_total: 0,
    item_count,
    buyer_id: userDoc?.uid || 'guest',
    buyer_email: userDoc?.email || '',
    address: userDoc?.addresses?.[0],
    customer_id: userDoc?.customer_id || '',
    payment: { type: 0, saved: true },
    product_ids: items.map((item) => item.product_id),
  } as unknown as OrderDocument;

  return {
    amount: calculatedTotal,
    order,
    metadata: metaData(order, isGuest)
  };
}

export async function getUpdatePaymentIntentArgs({
  pi_id,
  address,
  items,
  userDoc,
  userEmail,
  coupons,
  tax,
  taxPerItem,
  affirm,
  isGuest
}: {
  coupons: Coupon[];
  pi_id: string;
  address: AddressDocument;
  items: Array<CartItem & { rate_id?: string }>;
  userDoc?: UserDocument;
  userEmail: string;
  tax: number;
  taxPerItem?: Map<string, number>;
  affirm: boolean;
  isGuest: boolean;
}): Promise<UpdatePaymentArgs> {
  const id = getOrderId();
  if (!id) throw new Error('Could not get order id');

  const c = coupons?.[0]; // just using 1 coupon for MVP
  const coupon: OrderDocument['coupon'] = c
    ? {
        id: c.id,
        code: c.code,
        name: c.name,
        amount_off: c.amount_off,
        percent_off: c.percent_off,
        off: undefined,
      }
    : undefined;

  const sellers = items.reduce<{ [key: string]: OrderItemDocument[] }>(
    (acc, item) => {
      const orderItem = cartItemToOrderItem(item);
      orderItem.product_tax = taxPerItem?.get(item.product_id) ?? 0;
      if (acc[item.seller_id]) {
        acc[item.seller_id].push(orderItem);
      } else {
        acc[item.seller_id] = [orderItem];
      }
      return acc;
    },
    {}
  );

  const item_count = items.reduce((acc, item) => acc + item.qty, 0);

  const itemsTotal = items.reduce((acc, item) => {
    return acc + item.product_cost * item.qty;
  }, 0);

  const discountTotal = (() => {
    if (!coupon) return 0;
    if (!!coupon.amount_off) {
      return coupon.amount_off;
    }
    if (!!coupon.percent_off) {
      const percentOff = coupon.percent_off;
      return itemsTotal * (percentOff / 100);
    }
    return 0;
  })();

  const subtotal = itemsTotal - discountTotal;

  const shippingTotal = items.reduce((acc, item) => {
    return acc + (item.shipping_cost ?? 0);
  }, 0);

  const total = subtotal + shippingTotal + tax;
  // Do NOT mint an order number here. The real order_num is generated exactly
  // once, server-side, by the paymentSuccess webhook (generateOrderNumber), and
  // that is the only value ever persisted. Minting a "temp" number here caused
  // GB #1404: it burned the seller's order counter on every attempt, and it was
  // embedded in the confirmation redirect payload, so the success page showed a
  // throwaway number one lower than the real order whenever the page beat the
  // webhook. Empty string satisfies the required (string | number) schema field
  // and is falsy, so the confirmation page renders no number until the real
  // order loads — instead of a soon-to-be-wrong one.
  const order_num = '';

  // Create the order object with guest or authenticated user data
  const order = {
    address,
    buyer_id: userDoc?.uid || "Guest",
    buyer_email: userEmail,
    coupon,
    customer_id: userDoc?.customer_id || "",
    donate: false,
    id,
    order_num,
    item_count,
    payment: { type: 0, saved: true },
    product_ids: items.map((item) => item.product_id),
    sellers,
    shipping_total: shippingTotal,
    subtotal,
    total,
    tax,
  } satisfies MinimalOrderDocument;

  // Create metadata with guest information if available
  const metadata = {
    ...metaData(order, isGuest)
  };

  return {
    pi_id,
    address,
    amount: total,
    order,
    metadata,
    affirm,
  };
}

export function formatCurrency(number: number, currency = 'USD') {
  const upper = currency.toUpperCase();
  const locale = 'en-US';

  if (upper === 'CAD')
    return (
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
      }).format(number) + ' CAD'
    );

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: upper,
  }).format(number);
}

export function formatPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return phoneNumber;
  try {
    const cleaned = parsePhoneNumber(phoneNumber);
    if (!cleaned) return phoneNumber;
    return `+${cleaned.countryCallingCode} ${cleaned.formatNational()}`;
  } catch (e) {
    return phoneNumber;
  }
}

export function obfuscatePhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return phoneNumber;
  try {
    const cleaned = formatPhoneNumber(phoneNumber);
    const digitsOnly = cleaned.replace(/\D/g, ''); // Remove non-digit characters
    
    if (digitsOnly.length >= 4) {
      const beginningDigits = digitsOnly[0] + digitsOnly[1] + digitsOnly[2];
      const lastDigit = digitsOnly[digitsOnly.length - 1];
      const obfuscatedMiddle = '#'.repeat(digitsOnly.length - 3); // -2 to keep first and last digits

      // Reconstruct the obfuscated phone number
      return beginningDigits + obfuscatedMiddle + lastDigit;
    }
    return phoneNumber; // If the phone number is too short, return it as is
    
  } catch (e) {
    return phoneNumber;
  }
}

export function obfuscateEmailAddress(email: string) {
  if (!email) return email;
  try {
    const atIndex = email.indexOf('@');

    if (atIndex > 0) {
      const username = email.substring(0, atIndex);
      const obfuscatedUsername = obfuscateString(username);

      const domain = email.substring(atIndex);      
      const dotIndex = domain.lastIndexOf('.');

      if (dotIndex >= 0) {
        const subdomain = domain.substring(0, dotIndex);
        const tld = domain.substring(dotIndex);
        const emailEnding = subdomain.substring(0, 2) + '#'.repeat(subdomain.length - 2);
        return obfuscatedUsername + emailEnding + tld;
      }
      
      return obfuscatedUsername + domain;
    }
    
    return email;
    
  } catch (e) {
    return email;
  }
}

export function obfuscateString(input: string): string {
  if (input.length <= 2) {
    return input; // No need to obfuscate short strings
  }
  
  const firstTwoChars = input.substring(0, 2);
  const obfuscatedMiddle = '#'.repeat(input.length - 2);
  
  return firstTwoChars + obfuscatedMiddle;
}

export function formatTimestamp(timestamp: number) {
  if (timestamp.toString().length === 10) timestamp *= 1000;
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}
// Date/timestamp formatting for CT-pinned display lives in ./dateFormat (a
// pure, Firebase-free module) so it can be unit-tested without mocking
// Firebase — importing this barrel eagerly initializes Firebase.
// Re-exported here so `@util/index` consumers are unaffected.
export { formatDateCT, formatTimestampCT, daysUntilExpiration } from './dateFormat';
export function formatTime(timestamp: number) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatAuthError(error: FirebaseError) {
  const words = error.code.replace('auth/', '').replaceAll('-', ' ').split(' ');
  const capitalizedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return capitalizedWords.join(' ');
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export function getHostUrl() {
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
    const host = process.env.NEXT_PUBLIC_VERCEL_URL ?? 'gearfocus.com';
    return `https://${host}`;
  }
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'development') return 'https://staging.gearfocus.com';
  throw new Error('host not configured for current environment');
}

export const CURRENCY_MAP = {
  usd: 'US Dollar',
  cad: 'Canadian Dollar',
  eur: 'Euro',
  aud: 'Australian Dollar',
  hkd: 'Hong Kong Dollar',
  mxn: 'Mexican Peso',
  nzd: 'New Zealand Dollar',
  nok: 'Norwegian Krone',
  sgd: 'Singapore Dollar',
  chf: 'Swiss Franc',
  thb: 'Thai Baht',
  gbp: 'British Pound',
} as const;

export function getSellerRegistrationCurrencyOptions(): {
  label: string;
  value: string;
  id: string;
}[] {
  const allCurrency = getSellerRegistrationCountries().map((c) => c.currency);
  return [...new Set(allCurrency)].sort().map((c) => ({
    label: CURRENCY_MAP[c],
    value: c,
    id: c,
  }));
}

export function getSellerRegistrationCountries() {
  const countries = [
    {
      code: 'US',
      name: 'United States',
      currency: 'usd',
    },
    {
      code: 'CA',
      name: 'Canada',
      currency: 'cad',
    },
    { code: 'AT', name: 'Austria', currency: 'eur' },
    {
      code: 'AU',
      name: 'Australia',
      currency: 'aud',
    },
    { code: 'BE', name: 'Belgium', currency: 'eur' },
    { code: 'BG', name: 'Bulgaria', currency: 'eur' },
    { code: 'HR', name: 'Croatia', currency: 'eur' },
    { code: 'CY', name: 'Cyprus', currency: 'eur' },
    {
      code: 'CZ',
      name: 'Czech Republic',
      currency: 'eur',
    },
    { code: 'DK', name: 'Denmark', currency: 'eur' },
    { code: 'EE', name: 'Estonia', currency: 'eur' },
    { code: 'FI', name: 'Finland', currency: 'eur' },
    { code: 'FR', name: 'France', currency: 'eur' },
    { code: 'DE', name: 'Germany', currency: 'eur' },
    { code: 'GR', name: 'Greece', currency: 'eur' },
    {
      code: 'HK',
      name: 'Hong Kong',
      currency: 'hkd',
    },
    { code: 'HU', name: 'Hungary', currency: 'eur' },
    { code: 'IE', name: 'Ireland', currency: 'eur' },
    { code: 'IT', name: 'Italy', currency: 'eur' },
    { code: 'LV', name: 'Latvia', currency: 'eur' },
    { code: 'LT', name: 'Lithuania', currency: 'eur' },
    { code: 'LU', name: 'Luxembourg', currency: 'eur' },
    { code: 'MT', name: 'Malta', currency: 'eur' },
    {
      code: 'MX',
      name: 'Mexico',
      currency: 'mxn',
    },
    { code: 'NL', name: 'Netherlands', currency: 'eur' },
    {
      code: 'NZ',
      name: 'New Zealand',
      currency: 'nzd',
    },
    {
      code: 'NO',
      name: 'Norway',
      currency: 'nok',
    },
    { code: 'PL', name: 'Poland', currency: 'eur' },
    { code: 'PT', name: 'Portugal', currency: 'eur' },
    { code: 'RO', name: 'Romania', currency: 'eur' },
    {
      code: 'SG',
      name: 'Singapore',
      currency: 'sgd',
    },
    { code: 'SK', name: 'Slovakia', currency: 'eur' },
    { code: 'SI', name: 'Slovenia', currency: 'eur' },
    { code: 'ES', name: 'Spain', currency: 'eur' },
    { code: 'SE', name: 'Sweden', currency: 'eur' },
    {
      code: 'CH',
      name: 'Switzerland',
      currency: 'chf',
    },
    {
      code: 'TH',
      name: 'Thailand',
      currency: 'thb',
    },
    {
      code: 'GB',
      name: 'United Kingdom',
      currency: 'gbp',
    },
  ] as const;
  return countries;
}

export const offerStateToText = (state: number) => {
  switch (state) {
    case 1:
      return 'Accepted';
    case 2:
      return 'Declined';
    case 3:
      return 'Cancelled';
    default:
      return 'Pending';
  }
};

export const timeAgo = (timestamp?: number): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (!Intl || typeof Intl.RelativeTimeFormat !== 'function')
    return date.toLocaleString(); // unsupported browser
  const formatter = new Intl.RelativeTimeFormat('en', { style: 'narrow' });
  const ranges = {
    year: 3600 * 24 * 365,
    years: 3600 * 24 * 365,
    month: 3600 * 24 * 30,
    months: 3600 * 24 * 30,
    week: 3600 * 24 * 7,
    weeks: 3600 * 24 * 7,
    day: 3600 * 24,
    days: 3600 * 24,
    hour: 3600,
    hours: 3600,
    minute: 60,
    minutes: 60,
    second: 1,
    seconds: 1,
    quarter: 3600 * 24 * 91,
    quarters: 3600 * 24 * 91,
  } as const;
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;
  // Use singular forms for formatting
  const units: Intl.RelativeTimeFormatUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];
  for (const unit of units) {
    // Use plural form for lookup since our ranges object uses both
    const rangeValue = ranges[`${unit}s` as keyof typeof ranges];
    if (rangeValue && rangeValue < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / rangeValue;
      return formatter.format(Math.round(delta), unit);
    }
  }
  return 'just now'; // Default fallback
};

// Hours minutes and seconds until timestamp is reached in the format #H #M #S
export const timeRemaining = (timestamp?: number): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const secondsRemaining = (date.getTime() - Date.now()) / 1000;

  const hours = Math.floor(secondsRemaining / 3600);
  const minutes = Math.floor((secondsRemaining % 3600) / 60);
  const seconds = Math.floor(secondsRemaining % 60);

  const timeRemaining = `${hours}h ${minutes}m ${seconds}s`;
  return timeRemaining;
};

export function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
}

export function isTablet() {
  return typeof window !== 'undefined' && window.innerWidth <= 1024;
}

export function isDesktop() {
  return typeof window !== 'undefined' && window.innerWidth > 1024;
}

export function isLargeDesktop() {
  return typeof window !== 'undefined' && window.innerWidth > 1280;
}

export const debounce = <T extends (...args: any[]) => any>(func: T, delay: number) => {
  let timerId: ReturnType<typeof setTimeout>;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const capitalize = (word?: string): string => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const diffInDaysFromNow = (date: Date): number => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return diff / (1000 * 3600 * 24);
};

export const timestampToDatetimelocal = (timestamp: number): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  return `${year}-${
    month < 10 ? `0${month}` : month
  }-${day}T${hours}:${minutes}:${seconds}`;
};

export const datetimeLocalToTimestamp = (datetimeLocal: string): number => {
  const date = new Date(datetimeLocal);
  return date.getTime();
};

export const getDurationInDays = (start: number, end: number): number => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate.getTime() - startDate.getTime();
  return Math.floor(diff / (1000 * 3600 * 24));
};

export const timestampFromDuration = (duration: number, start: string): number => {
  const now = new Date(start);
  const timestamp = now.getTime() + duration * 1000 * 3600 * 24;
  return timestamp;
};

export const getEndDate = (duration: number, start: string): string => {
  const date = new Date(timestampFromDuration(duration, start));
  // return date and time
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

interface DateParts {
  date: string;
  hour: string;
  minute: string;
  AMPM: 'AM' | 'PM';
}

export const getDatePartsFromTimestamp = (timestamp?: number): DateParts | undefined => {
  if (!timestamp) return;
  const date = new Date(timestamp);
  const hour = date.getHours();
  const minute = date.getMinutes();

  const hourIn12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM

  return {
    date: date.toISOString().split('T')[0],
    hour: hourIn12 < 10 ? `0${hourIn12}` : `${hourIn12}`,
    minute: minute < 10 ? `0${minute}` : `${minute}`,
    AMPM: hour < 12 ? 'AM' : 'PM',
  };
};

export const inBetween = (startTime: string, duration: string): boolean => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(timestampFromDuration(Number(duration), startTime));
  return now.getTime() > start.getTime() && now.getTime() < end.getTime();
};
